import {
  INVENTORY_CONDITION,
  INVENTORY_CONDITION_LABELS,
  InventoryCondition,
} from './inventory-values';

export const PRODUCT_CATEGORIES = [
  'sealed',
  'deck_box',
  'binder',
  'sleeves',
  'playmat',
  'accessory',
  'other',
] as const;

export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export const PRODUCT_CATEGORY_LABELS: Record<ProductCategory, string> = {
  sealed: 'Produto Selado / Booster',
  deck_box: 'Deck Box / Porta Decks',
  binder: 'Pasta / Fichário',
  sleeves: 'Sleeves / Protetores',
  playmat: 'Playmat / Tapete',
  accessory: 'Acessórios / Dados',
  other: 'Outro',
};

export const PRODUCT_COMMERCIAL_CONDITIONS = [
  'sealed',
  'open_box',
  'used',
  'damaged_box',
] as const;

export type ProductCommercialCondition =
  (typeof PRODUCT_COMMERCIAL_CONDITIONS)[number];

export const PRODUCT_COMMERCIAL_CONDITION_LABELS: Record<
  ProductCommercialCondition,
  string
> = {
  sealed: 'Lacrado / Novo',
  open_box: 'Caixa Aberta / Sem uso',
  used: 'Usado',
  damaged_box: 'Embalagem Danificada',
};

export type ProductOfferCondition =
  InventoryCondition | ProductCommercialCondition;

export const PRODUCT_CONDITIONS = [
  ...Object.values(INVENTORY_CONDITION),
  ...PRODUCT_COMMERCIAL_CONDITIONS,
] as const;
export const PRODUCT_CONDITION_LABELS: Record<ProductOfferCondition, string> = {
  ...INVENTORY_CONDITION_LABELS,
  ...PRODUCT_COMMERCIAL_CONDITION_LABELS,
};

export interface ProductImage {
  imageUrl: string;
  altText: string;
}

export interface Product {
  createdAt?: string;
  updatedAt?: string;
  id: string;
  game: { id: string; name: string; slug: string } | null;
  name: string;
  slug: string;
  description: string;
  category: ProductCategory;
  isPlayable: boolean;
  manufacturer?: string | null;
  status: 'active' | 'paused' | 'archived';
  primaryImage?: ProductImage | null;
  widthCm?: number | null;
  heightCm?: number | null;
  lengthCm?: number | null;
  minimumPrice?: string | null;
  originalMinimumPrice?: string | null;
  availableQuantity: number;
}

export interface ProductOffer {
  id: string;
  product: Product;
  store: { id: string; name: string; slug: string };
  price: string;
  originalPrice?: string | null;
  promotionEndsAt?: string;
  imageUrl?: string | null;
  sellerBarcode?: string | null;
  sellerSku?: string | null;
  widthCm?: number | null;
  heightCm?: number | null;
  lengthCm?: number | null;
  quantity: number;
  condition: ProductOfferCondition;
  status: 'active' | 'paused';
}

export interface CreateProductInput {
  condition?: ProductOfferCondition;
  sellerSku?: string;
  sellerBarcode?: string;
  price?: number;
  quantity?: number;
  gameId?: string;
  collectionId?: string;
  name: string;
  description: string;
  category: ProductCategory;
  isPlayable?: boolean;
  manufacturer?: string;
  gtin?: string;
  weightGrams?: number;
  heightCm?: number;
  widthCm?: number;
  lengthCm?: number;
  images: ProductImage[];
  imageFile?: File;
}

export interface CreateProductOfferInput {
  productId: string;
  variantId?: string;
  price: number;
  quantity: number;
  condition: ProductOfferCondition;
  sellerSku?: string;
  sellerBarcode: string;
  widthCm: number;
  heightCm: number;
  lengthCm: number;
  imageFile?: File;
  clientReference?: string;
}

export interface UpdateProductOfferInput {
  price?: number;
  quantity?: number;
  status?: 'active' | 'paused';
  sellerBarcode?: string;
  widthCm?: number;
  heightCm?: number;
  lengthCm?: number;
}

export const PRODUCT_OFFERS_PAGE_SIZE = 10;

export const PRODUCT_STATUS_LABELS: Record<Product['status'], string> = {
  active: 'Ativo',
  paused: 'Pausado',
  archived: 'Arquivado',
};

export const PRODUCT_DIMENSION_MAX_CM = 99_999_999.99;
